import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { api, type Expense, type Reimbursement, type HsaAccount } from '@/api/client';
import { AccessibleDialog } from '@/components/AccessibleDialog';

interface ReimburseExpenseModalProps {
  expense: Expense;
  editingReimbursement: Reimbursement | null;
  onClose: () => void;
  onSaved: () => void;
}

export function ReimburseExpenseModal({
  expense,
  editingReimbursement,
  onClose,
  onSaved,
}: ReimburseExpenseModalProps) {
  const [accounts, setAccounts] = useState<HsaAccount[]>([]);
  const [form, setForm] = useState({
    amount: editingReimbursement?.amount.toString() || '',
    hsaAccountId: editingReimbursement?.hsaAccountId || '',
    reimbursementDate: editingReimbursement?.reimbursementDate || new Date().toISOString().split('T')[0],
    notes: editingReimbursement?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.getHsaAccounts().then((data) => {
      if (data) setAccounts(data);
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    if (!form.amount || !form.hsaAccountId || !form.reimbursementDate) {
      setError('Amount, HSA account, and date are required');
      return;
    }

    const amount = parseFloat(form.amount);
    if (amount <= 0) {
      setError('Amount must be greater than zero');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (editingReimbursement) {
        await api.updateReimbursement(editingReimbursement.id, {
          amount,
          reimbursementDate: form.reimbursementDate,
          notes: form.notes || undefined,
        });
      } else {
        await api.reimburseExpense(expense.id, {
          amount,
          hsaAccountId: form.hsaAccountId,
          reimbursementDate: form.reimbursementDate,
          notes: form.notes || undefined,
        });
      }
      toast.success(editingReimbursement ? 'Reimbursement updated' : 'Reimbursement recorded');
      onSaved();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save reimbursement');
      setError(err?.message || 'Failed to save reimbursement');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

  return (
    <AccessibleDialog
      isOpen={true}
      onClose={onClose}
      labelledById="reimburse-modal-title"
      className="bg-white rounded-lg w-full max-w-[450px] shadow-xl"
    >
        <div className="border-b border-[#E2E8F0] px-6 py-4">
          <h2 id="reimburse-modal-title" className="text-[18px] font-semibold text-[#1B365D]">
            {editingReimbursement ? 'Edit Reimbursement' : 'Record Reimbursement'}
          </h2>
          <p className="text-[13px] text-[#718096] mt-1">
            {expense.provider} - {formatCurrency(expense.amount)}
            {expense.amountRemaining > 0 && !editingReimbursement && (
              <span> ({formatCurrency(expense.amountRemaining)} remaining)</span>
            )}
          </p>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-[13px] text-red-700">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="reimburse-hsa-account" className="block text-[13px] font-semibold text-[#4A5568] mb-1.5">
              HSA Account *
            </label>
            <select
              id="reimburse-hsa-account"
              value={form.hsaAccountId}
              onChange={(e) => setForm({ ...form, hsaAccountId: e.target.value })}
              className="w-full h-10 px-3 text-[14px] border border-[#E2E8F0] rounded focus:outline-none focus:border-[#1B365D] focus:border-2"
              disabled={!!editingReimbursement}
            >
              <option value="">Select HSA account</option>
              {accounts.filter(a => a.status === 'Active').map((acct) => (
                <option key={acct.id} value={acct.id}>
                  {acct.name}{acct.custodian ? ` (${acct.custodian})` : ''}
                </option>
              ))}
            </select>
            {accounts.length === 0 && (
              <p className="text-[12px] text-[#718096] mt-1">
                No HSA accounts found. Add one on the Accounts page first.
              </p>
            )}
          </div>

          <div>
            <label htmlFor="reimburse-amount" className="block text-[13px] font-semibold text-[#4A5568] mb-1.5">
              Amount *
            </label>
            <input
              id="reimburse-amount"
              type="number"
              step="0.01"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="w-full h-10 px-3 text-[14px] border border-[#E2E8F0] rounded focus:outline-none focus:border-[#1B365D] focus:border-2"
              placeholder="0.00"
            />
          </div>

          <div>
            <label htmlFor="reimburse-date" className="block text-[13px] font-semibold text-[#4A5568] mb-1.5">
              Reimbursement Date *
            </label>
            <input
              id="reimburse-date"
              type="date"
              value={form.reimbursementDate}
              onChange={(e) => setForm({ ...form, reimbursementDate: e.target.value })}
              className="w-full h-10 px-3 text-[14px] border border-[#E2E8F0] rounded focus:outline-none focus:border-[#1B365D] focus:border-2"
            />
          </div>

          <div>
            <label htmlFor="reimburse-notes" className="block text-[13px] font-semibold text-[#4A5568] mb-1.5">
              Notes
            </label>
            <textarea
              id="reimburse-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full px-3 py-2 text-[14px] border border-[#E2E8F0] rounded focus:outline-none focus:border-[#1B365D] focus:border-2"
              rows={2}
              placeholder="Optional notes"
            />
          </div>
        </div>

        <div className="border-t border-[#E2E8F0] px-6 py-4 flex justify-end gap-3">
          <button
            className="px-4 py-2 text-[14px] font-semibold text-[#1B365D] border border-[#1B365D] rounded hover:bg-[#F7FAFC]"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 text-[14px] font-semibold text-white bg-[#1B365D] rounded hover:bg-[#152A4D] disabled:opacity-50"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : editingReimbursement ? 'Update' : 'Record Reimbursement'}
          </button>
        </div>
    </AccessibleDialog>
  );
}
